import { useState, useEffect } from 'react';

interface Transaction {
  id: number;
  stripe_payment_intent_id: string;
  stripe_subscription_id?: string;
  type: 'one-off' | 'subscription';
  status: string;
  amount: number;
  currency: string;
  payment_method_id?: string;
  payment_method_last4?: string;
  payment_method_brand?: string;
  flow_data: any;
  created_at: string;
}

interface ClientTransactionsProps {
  customerId: number;
  cachedData?: Transaction[];
  onDataLoaded?: (data: Transaction[]) => void;
}

export function ClientTransactions({ customerId, cachedData, onDataLoaded }: ClientTransactionsProps) {
  const [transactions, setTransactions] = useState<Transaction[]>(cachedData || []);
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedData) {
      setTransactions(cachedData);
      setIsLoading(false);
    } else {
      fetchTransactions();
    }
  }, [customerId, cachedData]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/customer/transactions');
      const result = await response.json();

      if (result.success) {
        setTransactions(result.data.transactions);
        onDataLoaded?.(result.data.transactions);
      } else {
        setError(result.error || 'Failed to fetch transactions');
        onDataLoaded?.([]);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      onDataLoaded?.([]);
      console.error('Transactions fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      succeeded: 'bg-green-50 text-green-700 border border-green-200',
      pending: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      failed: 'bg-red-50 text-red-700 border border-red-200',
      cancelled: 'bg-gray-50 text-gray-700 border border-gray-200',
    };

    const style = statusStyles[status as keyof typeof statusStyles] || statusStyles.pending;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${style}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeStyles = {
      'one-off': 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      'subscription': 'bg-purple-50 text-purple-700 border border-purple-200',
    };

    const style = typeStyles[type as keyof typeof typeStyles] || typeStyles['one-off'];

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${style}`}>
        {type === 'one-off' ? 'One-time' : 'Subscription'}
      </span>
    );
  };

  const getTransactionDescription = (transaction: Transaction) => {
    const formatRecipientName = (name: string) => {
      const words = name.trim().split(/\s+/);
      if (words.length === 1) {
        return words[0];
      } else {
        const firstName = words.slice(0, -1).join(' ');
        const lastNameInitial = words[words.length - 1].charAt(0) + '.';
        return `${firstName} ${lastNameInitial}`;
      }
    };

    const getRecipientNames = () => {
      const flowData = transaction.flow_data;
      
      if (transaction.type === 'subscription') {
        // Subscription transactions use singular 'recipient' field
        const recipient = flowData?.step1?.recipient;
        if (recipient && recipient.name) {
          return formatRecipientName(recipient.name);
        }
      } else {
        // One-off transactions use plural 'recipients' array
        const recipients = flowData?.step1?.recipients;
        if (recipients && recipients.length > 0) {
          return recipients.map((recipient: any) => formatRecipientName(recipient.name)).join(', ');
        }
      }
      return '';
    };

    const recipientNames = getRecipientNames();
    const recipientSuffix = recipientNames ? ` for ${recipientNames}` : '';

    if (transaction.type === 'subscription') {
      return `Annual Subscription${recipientSuffix}`;
    } else {
      return `One-off${recipientSuffix}`;
    }
  };

  const formatPaymentMethodBrand = (brand: string) => {
    const brandMap: { [key: string]: string } = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'amex': 'American Express',
      'discover': 'Discover',
      'diners': 'Diners Club',
      'jcb': 'JCB',
      'unionpay': 'UnionPay',
    };
    return brandMap[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const getPaymentMethodDisplay = (transaction: Transaction) => {
    if (transaction.payment_method_last4 && transaction.payment_method_brand) {
      return `${formatPaymentMethodBrand(transaction.payment_method_brand)} •••• ${transaction.payment_method_last4}`;
    }
    return 'Payment method not available';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p>{error}</p>
          <button
            onClick={fetchTransactions}
            className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {transactions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h4>
          <p className="text-gray-600">
            Your purchase history will appear here once you make your first order.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {transactions.map((transaction) => (
            <div
              key={transaction.id}
              className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-white"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-3">
                    {getTypeBadge(transaction.type)}
                    {getStatusBadge(transaction.status)}
                  </div>
                  
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {getTransactionDescription(transaction)}
                  </h4>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>
                      <span className="font-medium">Order Date:</span>{' '}
                      {new Date(transaction.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                    <p>
                      <span className="font-medium">Payment Method:</span>{' '}
                      {getPaymentMethodDisplay(transaction)}
                    </p>
                    <p>
                      <span className="font-medium">Payment ID:</span>{' '}
                      <span className="font-mono text-xs">
                        {transaction.stripe_payment_intent_id}
                      </span>
                    </p>
                    {transaction.stripe_subscription_id && (
                      <p>
                        <span className="font-medium">Subscription ID:</span>{' '}
                        <span className="font-mono text-xs">
                          {transaction.stripe_subscription_id}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="ml-6 text-right">
                  <div className="text-xl font-bold text-gray-900">
                    {formatAmount(transaction.amount, transaction.currency)}
                  </div>
                  {transaction.type === 'subscription' && (
                    <div className="text-sm text-gray-500">per year</div>
                  )}
                </div>
              </div>

              {/* Order Details */}
              {/* {transaction.flow_data && transaction.flow_data.step1 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Order Details</h5>
                  <div className="text-sm text-gray-600">
                    {transaction.type === 'one-off' && transaction.flow_data.step1.recipients && (
                      <div>
                        <p className="font-medium">Recipients:</p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          {transaction.flow_data.step1.recipients.map((recipient: any, index: number) => (
                            <li key={index}>
                              {recipient.name} ({recipient.relationship})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {transaction.flow_data.step1.occasion && (
                      <p className="mt-2">
                        <span className="font-medium">Occasion:</span> {transaction.flow_data.step1.occasion}
                      </p>
                    )}
                    {transaction.flow_data.step1.date && (
                      <p>
                        <span className="font-medium">Event Date:</span>{' '}
                        {new Date(transaction.flow_data.step1.date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )} */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}